import { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Edit2, Users, FileText, CreditCard, Wrench, Clock, Copy, Check, Loader2, ExternalLink, Trash2, X, Plus, Download, CheckCircle2, AlertTriangle } from 'lucide-react';
import { formatCurrency, formatDate } from '../utils/formatters';
import { supabase } from '../lib/supabase';
import type { TenantLink, MaintenanceTicket } from '../context/types';

export default function PropertyDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('overview');
    const tabOrder = ['overview', 'tenants', 'maintenance', 'payments'];
    const prevTabRef = useRef<string>('overview');
    const direction = tabOrder.indexOf(activeTab) >= tabOrder.indexOf(prevTabRef.current) ? 1 : -1;
    const [property, setProperty] = useState<any>(null);
    const [tenantLinks, setTenantLinks] = useState<TenantLink[]>([]);
    const [payments, setPayments] = useState<any[]>([]);
    const [tickets, setTickets] = useState<MaintenanceTicket[]>([]);
    const [loading, setLoading] = useState(true);

    const [copied, setCopied] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editName, setEditName] = useState('');
    const [editAddress, setEditAddress] = useState('');
    const [editRent, setEditRent] = useState('');
    const [editDueDay, setEditDueDay] = useState('1');

    // Invite state for adding new tenant link
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteName, setInviteName] = useState('');
    const [isInviting, setIsInviting] = useState(false);

    // Payments tab state
    const [paymentFilter, setPaymentFilter] = useState<'all' | 'paid' | 'pending' | 'overdue'>('all');
    const [isAddPaymentOpen, setIsAddPaymentOpen] = useState(false);
    const [newPayTenantLinkId, setNewPayTenantLinkId] = useState('');
    const [newPayAmount, setNewPayAmount] = useState('');
    const [newPayDueDate, setNewPayDueDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [newPayStatus, setNewPayStatus] = useState<'pending' | 'paid'>('pending');
    const [newPayNotes, setNewPayNotes] = useState('');
    const [isSavingPayment, setIsSavingPayment] = useState(false);

    const fetchAllData = async () => {
        if (!id) return;
        try {
            setLoading(true);
            
            // 1. Fetch Property
            const { data: propData, error: propError } = await supabase
                .from('properties')
                .select('*')
                .eq('id', id)
                .single();

            if (propError) throw propError;
            setProperty(propData);

            // 2. Fetch Tenant Links for this property
            const { data: tenantData } = await supabase
                .from('tenant_links')
                .select('*')
                .eq('property_id', id);
            setTenantLinks(tenantData || []);

            // 3. Fetch Maintenance Tickets
            const { data: ticketData } = await supabase
                .from('maintenance_tickets')
                .select('*')
                .eq('property_id', id)
                .order('created_at', { ascending: false });
            setTickets(ticketData || []);

            // 4. Fetch Payments (Placeholder)
            const { data: paymentData } = await supabase
                .from('rent_payments')
                .select('*')
                .eq('property_id', id)
                .order('due_date', { ascending: false });
            setPayments(paymentData || []);

        } catch (err) {
            console.error('Unexpected error fetching property data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllData();
    }, [id]);

    const activeTenant = tenantLinks.find(t => t.invite_status === 'active') || tenantLinks[0];

    const handleDeleteProperty = async () => {
        if (!id || !confirm('Are you sure you want to delete this property? This action cannot be undone.')) return;
        try {
            const { error } = await supabase.from('properties').delete().eq('id', id);
            if (error) throw error;
            navigate('/app/landlord/properties');
        } catch (err) {
            alert('Error deleting property');
        }
    };

    const handleUpdateProperty = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id) return;
        try {
            const { error } = await supabase
                .from('properties')
                .update({
                    name: editName,
                    address: editAddress,
                    rent_amount: Number(editRent),
                    due_day: Number(editDueDay)
                })
                .eq('id', id);
            
            if (error) throw error;
            setIsEditModalOpen(false);
            fetchAllData();
        } catch (err) {
            alert('Error updating property');
        }
    };

    const handleDeleteTenant = async (tenantLinkId: string) => {
        if (!confirm('Remove this tenant from the property?')) return;
        try {
            const { error } = await supabase
                .from('tenant_links')
                .delete()
                .eq('id', tenantLinkId);
            
            if (error) throw error;
            fetchAllData();
        } catch (err) {
            alert('Error removing tenant');
        }
    };

    const handleDeleteTicket = async (id: string) => {
        if (!confirm('Delete this maintenance ticket?')) return;
        try {
            const { error } = await supabase.from('maintenance_tickets').delete().eq('id', id);
            if (error) throw error;
            fetchAllData();
        } catch (err) {
            alert('Error deleting ticket');
        }
    };

    // ── Payments handlers ─────────────────────────────────────────────────
    const handleMarkPaid = async (paymentId: string) => {
        try {
            const { error } = await supabase
                .from('rent_payments')
                .update({ status: 'paid', paid_date: new Date().toISOString().slice(0, 10) })
                .eq('id', paymentId);
            if (error) throw error;
            fetchAllData();
        } catch (err) {
            console.error('[mark paid] error:', err);
            alert('Failed to mark payment as paid.');
        }
    };

    const handleDeletePayment = async (paymentId: string) => {
        if (!confirm('Delete this payment record?')) return;
        try {
            const { error } = await supabase.from('rent_payments').delete().eq('id', paymentId);
            if (error) throw error;
            fetchAllData();
        } catch (err) {
            alert('Error deleting payment');
        }
    };

    const openAddPaymentModal = () => {
        const active = tenantLinks.find(t => t.invite_status === 'active');
        setNewPayTenantLinkId(active?.id || tenantLinks[0]?.id || '');
        setNewPayAmount(property?.rent_amount?.toString() || '');
        setNewPayDueDate(new Date().toISOString().slice(0, 10));
        setNewPayStatus('pending');
        setNewPayNotes('');
        setIsAddPaymentOpen(true);
    };

    const handleCreatePayment = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id || !newPayAmount) return;
        setIsSavingPayment(true);
        try {
            const periodMonth = new Date(newPayDueDate);
            periodMonth.setDate(1);
            const { error } = await supabase.from('rent_payments').insert({
                property_id: id,
                tenant_link_id: newPayTenantLinkId || null,
                amount: Number(newPayAmount),
                due_date: newPayDueDate,
                paid_date: newPayStatus === 'paid' ? new Date().toISOString().slice(0, 10) : null,
                status: newPayStatus,
                period_month: periodMonth.toISOString().slice(0, 10),
                notes: newPayNotes || null,
            });
            if (error) throw error;
            setIsAddPaymentOpen(false);
            fetchAllData();
        } catch (err: any) {
            alert('Error creating payment: ' + (err.message || err));
        } finally {
            setIsSavingPayment(false);
        }
    };

    const handleExportPaymentsCsv = () => {
        if (payments.length === 0) return;
        const rows = payments.map(p => ({
            period_month: p.period_month || '',
            due_date: p.due_date || '',
            paid_date: p.paid_date || '',
            amount: p.amount,
            status: p.status,
            days_late: p.days_late ?? 0,
            notes: p.notes || '',
        }));
        const headers = Object.keys(rows[0]);
        const esc = (v: unknown) => {
            if (v === null || v === undefined) return '';
            const s = String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const csv = [headers.join(','), ...rows.map(r => headers.map(h => esc((r as any)[h])).join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${property?.name || 'property'}-payments-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const openEditModal = () => {
        setEditName(property.name);
        setEditAddress(property.address || '');
        setEditRent(property.rent_amount.toString());
        setEditDueDay(property.due_day.toString());
        setIsEditModalOpen(true);
    };

    const handleCreateInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id || !inviteEmail || !inviteName) return;

        setIsInviting(true);
        const inviteCode = Math.random().toString(36).substring(2, 8).toUpperCase();

        try {
            const { error } = await supabase
                .from('tenant_links')
                .insert({
                    property_id: id,
                    tenant_name: inviteName,
                    tenant_email: inviteEmail,
                    invite_code: inviteCode,
                    invite_status: 'invited',
                    created_at: new Date().toISOString()
                });

            if (error) {
                alert('Error creating invite: ' + error.message);
            } else {
                setInviteEmail('');
                setInviteName('');
                fetchAllData();
            }
        } catch (err) {
            console.error('Invite error:', err);
        } finally {
            setIsInviting(false);
        }
    };

    const handleTabChange = (tabId: string) => {
        prevTabRef.current = activeTab;
        setActiveTab(tabId);
    };

    const copyToClipboard = (code: string) => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (loading) {
        return (
            <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--color-bg)' }}>
                <Loader2 className="animate-spin" size={48} color="var(--color-primary)" />
            </div>
        );
    }

    if (!property) return <div className="page" style={{ padding: '4rem', textAlign: 'center' }}>Property not found</div>;

    const tabs = [
        { id: 'overview', label: 'Overview', icon: FileText },
        { id: 'tenants', label: 'Tenants', icon: Users },
        { id: 'maintenance', label: 'Maintenance', icon: Wrench },
        { id: 'payments', label: 'Payments', icon: CreditCard },
    ];

    return (
        <div className="page dashboard-page">
            <div className="breadcrumb" style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9375rem', fontWeight: 500, color: 'var(--color-text-tertiary)' }}>
                <Link to="/app/landlord/properties" style={{ color: 'inherit', textDecoration: 'none', transition: 'color 0.2s' }} className="breadcrumb-link-hover">Properties</Link>
                <ChevronRight size={14} />
                <span style={{ color: '#1a1d2e', fontWeight: 700 }}>{property.name}</span>
            </div>

            <div className="dashboard-page-header">
                <div className="dashboard-page-heading">
                    <h1 style={{ fontSize: '3rem', fontWeight: 900, margin: '0 0 12px 0', letterSpacing: '-0.05em', color: '#1a1d2e', display: 'flex', alignItems: 'center', gap: '20px' }}>
                        {property.name}
                        <span style={{ 
                            fontSize: '0.8125rem', 
                            padding: '8px 20px', 
                            borderRadius: '30px',
                            fontWeight: 800,
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            background: tenantLinks.some(t => t.invite_status === 'active') ? 'rgba(34, 197, 94, 0.08)' : 'rgba(234, 179, 8, 0.08)',
                            color: tenantLinks.some(t => t.invite_status === 'active') ? '#16a34a' : '#ca8a04',
                            border: '1px solid currentColor'
                        }}>
                            {tenantLinks.some(t => t.invite_status === 'active') ? 'Occupied' : 'Pending'}
                        </span>
                    </h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--color-text-secondary)', fontSize: '1.125rem', fontWeight: 500 }}>
                        <Clock size={20} opacity={0.6} /> 
                        <span>Portfolio added on {formatDate(property.created_at)}</span>
                    </div>
                </div>
                <div className="dashboard-page-actions">
                    <button className="btn btn-secondary" onClick={openEditModal} style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '14px', fontWeight: 700, padding: '0.875rem 1.5rem', background: '#fff', border: '1px solid #e2e8f0', color: '#1e293b' }}>
                        <Edit2 size={16} /> Edit
                    </button>
                    <button className="btn btn-secondary" onClick={handleDeleteProperty} style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '14px', fontWeight: 700, padding: '0.875rem 1.5rem', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.15)', background: 'rgba(239, 68, 68, 0.02)' }}>
                        <Trash2 size={16} /> Delete
                    </button>
                </div>
            </div>

            {/* Premium Tab Navigation (Segmented Control Style) */}
            <div className="dashboard-tabs">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => handleTabChange(tab.id)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '0.875rem 1.75rem',
                            background: activeTab === tab.id ? '#fff' : 'transparent',
                            border: 'none',
                            color: activeTab === tab.id ? 'var(--color-primary)' : 'var(--color-text-tertiary)',
                            fontWeight: 800,
                            fontSize: '0.9375rem',
                            cursor: 'pointer',
                            borderRadius: '14px',
                            boxShadow: activeTab === tab.id ? '0 10px 15px -3px rgba(0,0,0,0.04), 0 4px 6px -2px rgba(0,0,0,0.05)' : 'none',
                            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                    >
                        <tab.icon size={18} />
                        {tab.label}
                    </button>
                ))}
            </div>

            <AnimatePresence mode="wait" initial={false}>
            {/* Tab: Overview */}
            {activeTab === 'overview' && (
              <motion.div
                key="overview"
                initial={{ x: direction * 40, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: direction * -40, opacity: 0 }}
                transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
              >
                <div className="responsive-grid-4">
                    <div style={{ background: '#fff', padding: '2rem', borderRadius: '24px', border: '1px solid var(--color-border-subtle)', boxShadow: 'var(--shadow-sm)' }}>
                        <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.125rem', fontWeight: 800 }}>Property Specs</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem', borderBottom: '1px solid var(--color-bg-subtle)', padding: '0.75rem 0' }}>
                                <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 500, flexShrink: 0, fontSize: '0.875rem' }}>Full Address</span>
                                <span style={{ fontWeight: 700, color: 'var(--color-text)', textAlign: 'right' as const, fontSize: '0.875rem', wordBreak: 'break-word' as const }}>{property.address || '—'}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem', borderBottom: '1px solid var(--color-bg-subtle)', padding: '0.75rem 0' }}>
                                <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 500, flexShrink: 0, fontSize: '0.875rem' }}>Monthly Rent</span>
                                <span style={{ fontWeight: 800, color: '#16a34a', fontSize: '1.125rem' }}>{formatCurrency(property.rent_amount)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem', padding: '0.75rem 0' }}>
                                <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 500, flexShrink: 0, fontSize: '0.875rem' }}>Due Day</span>
                                <span style={{ fontWeight: 700, fontSize: '0.875rem' }}>Day {property.due_day}</span>
                            </div>
                        </div>
                    </div>

                    <div style={{ background: '#fff', padding: '2rem', borderRadius: '24px', border: '1px solid var(--color-border-subtle)', boxShadow: 'var(--shadow-sm)' }}>
                        <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.125rem', fontWeight: 800 }}>Occupancy</h3>
                        {activeTenant ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <div className="avatar avatar--blue" style={{ width: '56px', height: '56px', fontSize: '1.25rem', fontWeight: 700 }}>
                                    {activeTenant.tenant_name[0]}
                                </div>
                                <div>
                                    <div style={{ fontWeight: 800, fontSize: '1.125rem' }}>{activeTenant.tenant_name}</div>
                                    <div style={{ fontSize: '0.875rem', color: 'var(--color-text-tertiary)' }}>{activeTenant.tenant_email}</div>
                                    <div style={{ marginTop: '12px', fontSize: '0.8125rem', color: 'var(--color-text-secondary)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <div><strong>Start:</strong> {activeTenant.lease_start ? formatDate(activeTenant.lease_start) : 'Not set'}</div>
                                        <div><strong>End:</strong> {activeTenant.lease_end ? formatDate(activeTenant.lease_end) : 'Not set'}</div>
                                    </div>
                                    <div style={{ marginTop: '12px' }}>
                                        <span className={`badge badge--${activeTenant.invite_status === 'active' ? 'green' : 'yellow'}`} style={{ fontSize: '0.7rem' }}>
                                            {activeTenant.invite_status === 'active' ? 'Lease Active' : 'Invite Sent'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                                <Users size={40} opacity={0.2} style={{ marginBottom: '1rem' }} />
                                <p style={{ color: 'var(--color-text-tertiary)', fontSize: '0.9375rem' }}>This property is currently vacant.</p>
                                <button className="btn btn-primary btn-sm" onClick={() => setActiveTab('tenants')} style={{ marginTop: '1rem' }}>Add Tenant</button>
                            </div>
                        )}
                    </div>

                    <div style={{ background: '#fff', padding: '2rem', borderRadius: '24px', border: '1px solid var(--color-border-subtle)', boxShadow: 'var(--shadow-sm)' }}>
                        <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.125rem', fontWeight: 800 }}>Maintenance Summary</h3>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <div>
                                <div style={{ fontSize: '2rem', fontWeight: 800 }}>{tickets.filter(t => t.status !== 'resolved').length}</div>
                                <div style={{ fontSize: '0.875rem', color: 'var(--color-text-tertiary)', fontWeight: 600 }}>Open Tickets</div>
                            </div>
                            <div>
                                <div style={{ fontSize: '2rem', fontWeight: 800 }}>{tickets.filter(t => t.status === 'resolved').length}</div>
                                <div style={{ fontSize: '0.875rem', color: 'var(--color-text-tertiary)', fontWeight: 600 }}>Resolved</div>
                            </div>
                        </div>
                        <button className="btn btn-primary btn-sm" onClick={() => navigate('/app/landlord/maintenance')} style={{ marginTop: '0.5rem', width: '100%' }}>Create Ticket</button>
                    </div>

                    <div style={{ background: '#fff', padding: '2rem', borderRadius: '24px', border: '1px solid var(--color-border-subtle)', boxShadow: 'var(--shadow-sm)' }}>
                        <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.125rem', fontWeight: 800 }}>Payment Overview</h3>
                        <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--color-bg-subtle)', paddingBottom: '0.75rem', marginBottom: '0.75rem' }}>
                            <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 500 }}>Last Payment</span>
                            <span style={{ fontWeight: 700, color: 'var(--color-green)' }}>{payments.length > 0 ? formatCurrency(payments[0].amount) : 'None'}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                            <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 500 }}>Status</span>
                            <span style={{ fontWeight: 700 }}>{payments.length > 0 ? 'Up to date' : 'Pending Setup'}</span>
                        </div>
                        <button className="btn btn-secondary btn-sm" onClick={() => setActiveTab('payments')} style={{ marginTop: '0.5rem', width: '100%' }}>View History</button>
                    </div>
                </div>
              </motion.div>
            )}

            {/* Tab: Tenants */}
            {activeTab === 'tenants' && (
              <motion.div
                key="tenants"
                initial={{ x: direction * 40, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: direction * -40, opacity: 0 }}
                transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
              >
                <div className="dashboard-grid-2">
                    <div style={{ background: '#fff', borderRadius: '24px', border: '1px solid var(--color-border-subtle)', overflow: 'hidden' }}>
                        <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--color-border-subtle)', display: 'flex', justifyContent: 'space-between' }}>
                            <h3 style={{ margin: 0, fontWeight: 800 }}>Tenant History</h3>
                        </div>
                        {tenantLinks.length === 0 ? (
                            <div style={{ padding: '4rem', textAlign: 'center' }}>
                                <Users size={48} opacity={0.1} style={{ marginBottom: '1rem' }} />
                                <p style={{ color: 'var(--color-text-tertiary)' }}>No tenants linked to this property yet.</p>
                            </div>
                        ) : (
                            <div className="responsive-table">
                            <table className="data-table" style={{ minWidth: '760px' }}>
                                <thead style={{ background: 'var(--color-bg-subtle)' }}>
                                    <tr>
                                        <th style={{ padding: '1.5rem' }}>Tenant</th>
                                        <th style={{ padding: '1.5rem' }}>Invite Status</th>
                                        <th style={{ padding: '1.5rem' }}>Invite Code</th>
                                        <th style={{ padding: '1.5rem' }}>Lease Period</th>
                                        <th style={{ padding: '1.5rem' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tenantLinks.map(link => (
                                        <tr key={link.id}>
                                            <td style={{ padding: '1.5rem' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <div className="avatar avatar--indigo" style={{ width: 40, height: 40, fontSize: 14 }}>{link.tenant_name[0]}</div>
                                                    <div>
                                                        <div style={{ fontWeight: 800 }}>{link.tenant_name}</div>
                                                        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-tertiary)' }}>{link.tenant_email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ padding: '1.5rem' }}>
                                                <span 
                                                    style={{ 
                                                        padding: '6px 12px', 
                                                        fontSize: '0.75rem', 
                                                        fontWeight: 800, 
                                                        borderRadius: '10px',
                                                        background: link.invite_status === 'active' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(234, 179, 8, 0.1)',
                                                        color: link.invite_status === 'active' ? '#16a34a' : '#ca8a04'
                                                    }}
                                                >
                                                    {link.invite_status.toUpperCase()}
                                                </span>
                                            </td>
                                            <td style={{ padding: '1.5rem' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <code style={{ background: 'var(--color-bg-subtle)', padding: '6px 12px', borderRadius: '8px', fontSize: '0.9375rem', fontWeight: 800 }}>{link.invite_code}</code>
                                                    <button onClick={() => copyToClipboard(link.invite_code)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-tertiary)' }}>
                                                        {copied ? <Check size={16} color="var(--color-green)" /> : <Copy size={16} />}
                                                    </button>
                                                </div>
                                            </td>
                                            <td style={{ padding: '1.5rem', fontWeight: 600, fontSize: '0.9375rem' }}>
                                                {link.lease_start ? `${formatDate(link.lease_start)}${link.lease_end ? ` - ${formatDate(link.lease_end)}` : ' (Ongoing)'}` : 'Not scheduled'}
                                            </td>
                                            <td style={{ padding: '1.5rem', textAlign: 'right' }}>
                                                <button onClick={() => handleDeleteTenant(link.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-red)', opacity: 0.6 }}>
                                                    <Trash2 size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                        )}
                    </div>

                    <div style={{ background: '#fff', padding: '2rem', borderRadius: '24px', border: '1px solid var(--color-border-subtle)' }}>
                        <h3 style={{ margin: '0 0 1.5rem 0', fontWeight: 800 }}>Invite New Tenant</h3>
                        <form onSubmit={handleCreateInvite} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            <div className="auth-field">
                                <label style={{ fontWeight: 700, fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: '8px', display: 'block' }}>Full Name</label>
                                <input 
                                    type="text" 
                                    placeholder="e.g. Alice Cooper" 
                                    value={inviteName}
                                    onChange={e => setInviteName(e.target.value)}
                                    required 
                                    style={{ padding: '0.75rem', borderRadius: '10px' }}
                                />
                            </div>
                            <div className="auth-field">
                                <label style={{ fontWeight: 700, fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: '8px', display: 'block' }}>Email Address</label>
                                <input 
                                    type="email" 
                                    placeholder="alice@example.com" 
                                    value={inviteEmail}
                                    onChange={e => setInviteEmail(e.target.value)}
                                    required 
                                    style={{ padding: '0.75rem', borderRadius: '10px' }}
                                />
                            </div>
                            <button type="submit" className="btn btn-primary" disabled={isInviting || !inviteEmail || !inviteName} style={{ height: '48px', borderRadius: '12px', fontWeight: 700 }}>
                                {isInviting ? 'Creating...' : 'Generate New Invite'}
                            </button>
                        </form>
                        <p style={{ marginTop: '1.5rem', fontSize: '0.8125rem', color: 'var(--color-text-tertiary)', lineHeight: 1.5 }}>
                            Inviting a tenant will generate a unique code for this property. The tenant must use this code when registration.
                        </p>
                    </div>
                </div>
              </motion.div>
            )}

            {/* Tab: Maintenance */}
            {activeTab === 'maintenance' && (
              <motion.div
                key="maintenance"
                initial={{ x: direction * 40, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: direction * -40, opacity: 0 }}
                transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
              >
                <div style={{ background: '#fff', borderRadius: '24px', border: '1px solid var(--color-border-subtle)', overflow: 'hidden' }}>
                    <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--color-border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ margin: 0, fontWeight: 800 }}>Repair Requests</h3>
                        <Link to="/app/landlord/maintenance" className="btn btn-secondary btn-sm" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            Full Board <ExternalLink size={14} />
                        </Link>
                    </div>
                    {tickets.length === 0 ? (
                        <div style={{ padding: '4rem', textAlign: 'center' }}>
                            <Wrench size={48} opacity={0.1} style={{ marginBottom: '1rem' }} />
                            <p style={{ color: 'var(--color-text-tertiary)' }}>No maintenance tickets for this property.</p>
                        </div>
                    ) : (
                        <div className="responsive-table">
                        <table className="data-table" style={{ minWidth: '700px' }}>
                            <thead style={{ background: 'var(--color-bg-subtle)' }}>
                                <tr>
                                    <th style={{ padding: '1.5rem', fontSize: '0.8125rem' }}>Ticket Title</th>
                                    <th style={{ padding: '1.5rem', fontSize: '0.8125rem' }}>Status</th>
                                    <th style={{ padding: '1.5rem', fontSize: '0.8125rem' }}>Priority</th>
                                    <th style={{ padding: '1.5rem', fontSize: '0.8125rem' }}>Requested On</th>
                                    <th style={{ padding: '1.5rem', width: '50px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {tickets.map(ticket => (
                                    <tr key={ticket.id}>
                                        <td style={{ padding: '1.5rem', fontWeight: 800, color: 'var(--color-text)' }}>{ticket.title}</td>
                                        <td style={{ padding: '1.5rem' }}>
                                            <span 
                                                style={{ 
                                                    padding: '6px 12px', 
                                                    fontSize: '0.75rem', 
                                                    fontWeight: 800, 
                                                    borderRadius: '10px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    background: ticket.status === 'resolved' ? 'rgba(34, 197, 94, 0.1)' : (ticket.status === 'in_progress' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(234, 179, 8, 0.1)'),
                                                    color: ticket.status === 'resolved' ? '#16a34a' : (ticket.status === 'in_progress' ? '#2563eb' : '#ca8a04'),
                                                    textTransform: 'uppercase'
                                                }}
                                            >
                                                <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }}></div>
                                                {ticket.status}
                                            </span>
                                        </td>
                                        <td style={{ padding: '1.5rem' }}>
                                            <span 
                                                style={{ 
                                                    padding: '4px 8px', 
                                                    fontSize: '0.75rem', 
                                                    fontWeight: 700, 
                                                    borderRadius: '6px',
                                                    background: ticket.priority === 'high' ? 'rgba(239, 68, 68, 0.1)' : (ticket.priority === 'medium' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(107, 114, 128, 0.1)'),
                                                    color: ticket.priority === 'high' ? '#ef4444' : (ticket.priority === 'medium' ? '#f59e0b' : '#6b7280'),
                                                }}
                                            >
                                                {ticket.priority.toUpperCase()}
                                            </span>
                                        </td>
                                        <td style={{ padding: '1.5rem', color: 'var(--color-text-tertiary)', fontWeight: 500, fontSize: '0.875rem' }}>
                                            {formatDate(ticket.created_at)}
                                        </td>
                                        <td style={{ padding: '1.5rem', textAlign: 'right' }}>
                                            <button onClick={() => handleDeleteTicket(ticket.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', opacity: 0.7 }} title="Delete Ticket">
                                                <Trash2 size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    )}
                </div>
              </motion.div>
            )}

            {/* Tab: Payments */}
            {activeTab === 'payments' && (() => {
                const totalCollected = payments.filter(p => p.status === 'paid').reduce((s, p) => s + Number(p.amount || 0), 0);
                const totalOutstanding = payments.filter(p => p.status === 'pending' || p.status === 'overdue').reduce((s, p) => s + Number(p.amount || 0), 0);
                const overdueCount = payments.filter(p => p.status === 'overdue').length;
                const filtered = paymentFilter === 'all' ? payments : payments.filter(p => p.status === paymentFilter);
                const tenantName = (linkId: string | null) => {
                    if (!linkId) return '—';
                    const t = tenantLinks.find(x => x.id === linkId);
                    return t?.tenant_name || t?.tenant_email || '—';
                };

                return (
                <motion.div
                    key="payments"
                    initial={{ x: direction * 40, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: direction * -40, opacity: 0 }}
                    transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                >
                    {/* Summary cards */}
                    <div className="overview-grid" style={{ marginBottom: '1.5rem' }}>
                        <div className="overview-card">
                            <div className="overview-card__header">
                                <span className="overview-card__title">Total Collected</span>
                                <div className="overview-card__icon" style={{ background: 'rgba(22,163,74,0.10)', color: '#16a34a' }}>
                                    <CheckCircle2 size={18} />
                                </div>
                            </div>
                            <div className="overview-card__value">{formatCurrency(totalCollected)}</div>
                            <div className="overview-card__label">All paid payments</div>
                        </div>
                        <div className="overview-card">
                            <div className="overview-card__header">
                                <span className="overview-card__title">Outstanding</span>
                                <div className="overview-card__icon" style={{ background: 'rgba(220,38,38,0.10)', color: '#dc2626' }}>
                                    <AlertTriangle size={18} />
                                </div>
                            </div>
                            <div className="overview-card__value">{formatCurrency(totalOutstanding)}</div>
                            <div className="overview-card__label">Pending + overdue</div>
                        </div>
                        <div className="overview-card">
                            <div className="overview-card__header">
                                <span className="overview-card__title">Overdue</span>
                                <div className="overview-card__icon" style={{ background: 'rgba(220,38,38,0.10)', color: '#dc2626' }}>
                                    <Clock size={18} />
                                </div>
                            </div>
                            <div className="overview-card__value">{overdueCount}</div>
                            <div className="overview-card__label">Past due date</div>
                        </div>
                    </div>

                    <div className="data-panel">
                        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border-subtle)', display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center', justifyContent: 'space-between' }}>
                            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800 }}>Payment history</h3>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <button onClick={handleExportPaymentsCsv} disabled={payments.length === 0} className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <Download size={14} /> CSV
                                </button>
                                <button onClick={openAddPaymentModal} className="btn btn-primary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <Plus size={14} /> Add payment
                                </button>
                            </div>
                        </div>

                        {/* Filter tabs */}
                        <div style={{ display: 'flex', gap: '0.375rem', padding: '0.75rem 1.5rem', borderBottom: '1px solid var(--color-border-subtle)', flexWrap: 'wrap' }}>
                            {(['all', 'paid', 'pending', 'overdue'] as const).map(f => {
                                const count = f === 'all' ? payments.length : payments.filter(p => p.status === f).length;
                                const active = paymentFilter === f;
                                return (
                                    <button
                                        key={f}
                                        onClick={() => setPaymentFilter(f)}
                                        style={{
                                            padding: '0.4rem 0.85rem',
                                            fontSize: '0.8125rem',
                                            fontWeight: 700,
                                            borderRadius: 999,
                                            cursor: 'pointer',
                                            textTransform: 'capitalize',
                                            border: active ? '1px solid #16a34a' : '1px solid var(--color-border)',
                                            background: active ? 'rgba(22,163,74,0.10)' : '#fff',
                                            color: active ? '#16a34a' : 'var(--color-text-secondary)',
                                        }}
                                    >
                                        {f} <span style={{ opacity: 0.6, marginLeft: 4 }}>({count})</span>
                                    </button>
                                );
                            })}
                        </div>

                        {filtered.length === 0 ? (
                            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
                                <CreditCard size={40} opacity={0.2} style={{ marginBottom: '0.75rem' }} />
                                <div>{payments.length === 0 ? 'No payment records yet.' : `No ${paymentFilter} payments.`}</div>
                            </div>
                        ) : (
                            <div className="responsive-table">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Tenant</th>
                                            <th>Period</th>
                                            <th>Amount</th>
                                            <th>Due</th>
                                            <th>Paid</th>
                                            <th>Status</th>
                                            <th style={{ textAlign: 'right' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map(pay => (
                                            <tr key={pay.id}>
                                                <td style={{ fontWeight: 600 }}>{tenantName(pay.tenant_link_id)}</td>
                                                <td style={{ color: 'var(--color-text-secondary)' }}>
                                                    {pay.period_month ? formatDate(pay.period_month).replace(/ \d{4}$/, m => m) : '—'}
                                                </td>
                                                <td style={{ fontWeight: 700 }}>{formatCurrency(Number(pay.amount))}</td>
                                                <td style={{ color: 'var(--color-text-secondary)' }}>{pay.due_date ? formatDate(pay.due_date) : '—'}</td>
                                                <td style={{ color: 'var(--color-text-secondary)' }}>{pay.paid_date ? formatDate(pay.paid_date) : '—'}</td>
                                                <td>
                                                    {pay.status === 'paid' && <span className="badge badge--green"><CheckCircle2 size={12} /> Paid</span>}
                                                    {pay.status === 'pending' && <span className="badge badge--yellow"><Clock size={12} /> Pending</span>}
                                                    {pay.status === 'overdue' && <span className="badge badge--red"><AlertTriangle size={12} /> Overdue</span>}
                                                </td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <div style={{ display: 'inline-flex', gap: 6 }}>
                                                        {pay.status !== 'paid' && (
                                                            <button
                                                                onClick={() => handleMarkPaid(pay.id)}
                                                                className="btn btn-secondary btn-sm"
                                                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                                                title="Mark as paid"
                                                            >
                                                                <Check size={12} /> Mark paid
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => handleDeletePayment(pay.id)}
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', opacity: 0.7, padding: 4 }}
                                                            title="Delete payment"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </motion.div>
                );
            })()}
            </AnimatePresence>
            {/* Add Payment Modal */}
            {isAddPaymentOpen && (
                <div className="dropdown-overlay dashboard-modal-shell" onClick={() => setIsAddPaymentOpen(false)} style={{ background: 'rgba(15, 23, 42, 0.6)', animation: 'fadeIn 0.2s ease-out' }}>
                    <div
                        className="auth-card dashboard-modal"
                        style={{ width: 'min(540px, 92vw)', borderRadius: '28px', padding: '3rem', boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.3)', animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                            <div>
                                <h3 className="auth-title" style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800 }}>Add Payment</h3>
                                <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>Record a new payment for this property</p>
                            </div>
                            <button
                                onClick={() => setIsAddPaymentOpen(false)}
                                style={{ background: '#f1f5f9', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', padding: '10px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleCreatePayment} className="auth-form" style={{ gap: '1.25rem', display: 'flex', flexDirection: 'column' }}>
                            {tenantLinks.length > 0 && (
                                <div className="auth-field">
                                    <label style={{ fontSize: '0.8125rem', fontWeight: 700, marginBottom: '0.5rem', display: 'block' }}>Tenant</label>
                                    <select
                                        value={newPayTenantLinkId}
                                        onChange={e => setNewPayTenantLinkId(e.target.value)}
                                        style={{ width: '100%', padding: '0.875rem', borderRadius: '12px', border: '1px solid var(--color-border)', fontSize: '0.9375rem', background: '#f8fafc' }}
                                    >
                                        <option value="">— None —</option>
                                        {tenantLinks.map(t => (
                                            <option key={t.id} value={t.id}>{t.tenant_name || t.tenant_email}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="dashboard-form-grid dashboard-form-grid--equal" style={{ gap: '1rem' }}>
                                <div className="auth-field">
                                    <label style={{ fontSize: '0.8125rem', fontWeight: 700, marginBottom: '0.5rem', display: 'block' }}>Amount (€)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={newPayAmount}
                                        onChange={e => setNewPayAmount(e.target.value)}
                                        required
                                        style={{ borderRadius: '12px', padding: '0.875rem', border: '1px solid var(--color-border)', fontSize: '0.9375rem', width: '100%', background: '#f8fafc' }}
                                    />
                                </div>
                                <div className="auth-field">
                                    <label style={{ fontSize: '0.8125rem', fontWeight: 700, marginBottom: '0.5rem', display: 'block' }}>Due date</label>
                                    <input
                                        type="date"
                                        value={newPayDueDate}
                                        onChange={e => setNewPayDueDate(e.target.value)}
                                        required
                                        style={{ borderRadius: '12px', padding: '0.875rem', border: '1px solid var(--color-border)', fontSize: '0.9375rem', width: '100%', background: '#f8fafc' }}
                                    />
                                </div>
                            </div>

                            <div className="auth-field">
                                <label style={{ fontSize: '0.8125rem', fontWeight: 700, marginBottom: '0.5rem', display: 'block' }}>Status</label>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    {(['pending', 'paid'] as const).map(s => (
                                        <button
                                            key={s}
                                            type="button"
                                            onClick={() => setNewPayStatus(s)}
                                            style={{
                                                flex: 1, padding: '0.75rem', borderRadius: 12, cursor: 'pointer',
                                                fontSize: '0.875rem', fontWeight: 700, textTransform: 'capitalize',
                                                border: newPayStatus === s ? '2px solid #16a34a' : '1px solid var(--color-border)',
                                                background: newPayStatus === s ? 'rgba(22,163,74,0.08)' : '#fff',
                                                color: newPayStatus === s ? '#16a34a' : 'var(--color-text-secondary)',
                                            }}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="auth-field">
                                <label style={{ fontSize: '0.8125rem', fontWeight: 700, marginBottom: '0.5rem', display: 'block' }}>Notes (optional)</label>
                                <textarea
                                    value={newPayNotes}
                                    onChange={e => setNewPayNotes(e.target.value)}
                                    rows={3}
                                    style={{ borderRadius: '12px', padding: '0.875rem', border: '1px solid var(--color-border)', fontSize: '0.9375rem', width: '100%', background: '#f8fafc', fontFamily: 'inherit', resize: 'vertical' }}
                                />
                            </div>

                            <div className="dashboard-modal__actions" style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                <button type="button" className="btn btn-secondary" onClick={() => setIsAddPaymentOpen(false)}>Cancel</button>
                                <button type="submit" className="btn btn-primary" disabled={isSavingPayment} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    {isSavingPayment && <Loader2 size={14} className="animate-spin" />}
                                    Save payment
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Property Modal */}
            {isEditModalOpen && (
                <div className="dropdown-overlay dashboard-modal-shell" onClick={() => setIsEditModalOpen(false)} style={{ background: 'rgba(15, 23, 42, 0.6)', animation: 'fadeIn 0.2s ease-out' }}>
                    <div
                        className="auth-card dashboard-modal"
                        style={{ width: 'min(540px, 92vw)', borderRadius: '28px', padding: '3rem', boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.3)', animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2.5rem' }}>
                            <div>
                                <h3 className="auth-title" style={{ margin: 0, fontSize: '1.75rem', fontWeight: 800, letterSpacing: '-0.02em' }}>Edit Property</h3>
                                <p style={{ fontSize: '0.9375rem', color: 'var(--color-text-secondary)', marginTop: '6px' }}>Update property details and rental terms</p>
                            </div>
                            <button
                                onClick={() => setIsEditModalOpen(false)}
                                style={{ background: '#f1f5f9', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', padding: '10px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleUpdateProperty} className="auth-form" style={{ gap: '2rem', display: 'flex', flexDirection: 'column' }}>
                            <div className="auth-field">
                                <label style={{ fontSize: '0.875rem', fontWeight: 700, marginBottom: '0.625rem', display: 'block', color: 'var(--color-text)' }}>Property Name</label>
                                <input
                                    type="text"
                                    value={editName}
                                    onChange={e => setEditName(e.target.value)}
                                    required
                                    style={{ borderRadius: '15px', padding: '1rem', border: '1px solid var(--color-border)', fontSize: '1rem', width: '100%', background: '#f8fafc' }}
                                />
                            </div>
                            <div className="auth-field">
                                <label style={{ fontSize: '0.875rem', fontWeight: 700, marginBottom: '0.625rem', display: 'block', color: 'var(--color-text)' }}>Complete Address</label>
                                <input
                                    type="text"
                                    value={editAddress}
                                    onChange={e => setEditAddress(e.target.value)}
                                    style={{ borderRadius: '15px', padding: '1rem', border: '1px solid var(--color-border)', fontSize: '1rem', width: '100%', background: '#f8fafc' }}
                                />
                            </div>
                            <div className="dashboard-form-grid dashboard-form-grid--equal" style={{ gap: '1.5rem' }}>
                                <div className="auth-field">
                                    <label style={{ fontSize: '0.875rem', fontWeight: 700, marginBottom: '0.625rem', display: 'block', color: 'var(--color-text)' }}>Monthly Rent (€)</label>
                                    <input
                                        type="number"
                                        value={editRent}
                                        onChange={e => setEditRent(e.target.value)}
                                        required
                                        style={{ borderRadius: '15px', padding: '1rem', border: '1px solid var(--color-border)', fontSize: '1rem', width: '100%', background: '#f8fafc' }}
                                    />
                                </div>
                                <div className="auth-field">
                                    <label style={{ fontSize: '0.875rem', fontWeight: 700, marginBottom: '0.625rem', display: 'block', color: 'var(--color-text)' }}>Rent Due Day</label>
                                    <select
                                        value={editDueDay}
                                        onChange={e => setEditDueDay(e.target.value)}
                                        style={{ width: '100%', padding: '1rem', borderRadius: '15px', border: '1px solid var(--color-border)', fontSize: '0.9375rem', background: '#f8fafc' }}
                                    >
                                        {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                                            <option key={d} value={d}>Day {d}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="dashboard-modal__actions" style={{ marginTop: '1rem', borderTop: '1px solid var(--color-border-subtle)', paddingTop: '2.5rem' }}>
                                <button type="button" className="btn btn-secondary" onClick={() => setIsEditModalOpen(false)} style={{ padding: '1rem 2rem', borderRadius: '15px', fontWeight: 600 }}>Cancel</button>
                                <button type="submit" className="btn btn-primary" style={{ padding: '1rem 3rem', borderRadius: '15px', fontWeight: 700, boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.3)' }}>Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
            `}</style>
        </div>
    );
}
